import { lazy, Suspense, useCallback, useRef } from 'react'
import { useWatchRoom } from '../hooks/useWatchRoom'
import { logEvent } from '../lib/analytics'

const InviteToWatchSheet = lazy(() => import('./InviteToWatchSheet').then(m => ({ default: m.InviteToWatchSheet })))

// ── InviteDoor — the ONE way to open "Watch together" from any show surface ─────
// 2026-10-12: the guest co-watch invite (text a link, they type a name, they're in the
// lobby with you — no account) has existed since 15.7x and worked, but version 17
// deleted the only page that mounted its sheet. 1 room and 0 guest joins in 30 days.
// This mounts it again behind a row in the show page's ⋯, the Home deck's ⋯ and a
// Tonight plan's ⋯, with the room-minting the old ShowDetail did inline.
export function InviteDoor({ show, user, profile, wl, onClose }) {
  const wr = useWatchRoom(user?.id)
  const pendingRef = useRef(null)
  const prog = wl?.progress?.[show?.id]

  const ensureRoomId = useCallback(async () => {
    if (!user?.id || !show?.id) return null
    if (pendingRef.current) return { id: pendingRef.current, created: false }
    const season = prog?.season || 1, episode = prog?.episode || 1
    const existing = await wr.findRoomForShow(show.id, { season, episode }).catch(() => null)
    if (existing) { await wr.joinRoom(existing.id).catch(() => {}); return { id: existing.id, created: false } }
    const room = await wr.createRoom({
      show_id: show.id, show_name: show.name, show_poster: show.poster_path || null,
      show_backdrop: show.backdrop_path || null, season, episode, episode_name: null,
    })
    pendingRef.current = room?.id || null
    logEvent('cowatch_room_created', { show_id: show.id, src: 'invite_door' })
    return room ? { id: room.id, created: true } : null
  }, [user?.id, show?.id, show?.name, show?.poster_path, show?.backdrop_path, prog?.season, prog?.episode, wr])

  // An invite that failed to send must not strand the host in an empty lobby.
  // Through the hook's member-shaped ending (end_watch_room RPC): the rooms table only
  // allows lobby / watching / done, so a direct status write would be refused.
  const onDiscardRoom = useCallback(async (rid) => {
    try { await wr.endRoom(rid) } catch {}
    if (pendingRef.current === rid) pendingRef.current = null
  }, [wr])

  if (!show || !user) return null
  return (
    <Suspense fallback={null}>
      <InviteToWatchSheet
        show={show} user={user}
        hostName={profile?.display_name || profile?.username || null}
        ensureRoomId={ensureRoomId} onDiscardRoom={onDiscardRoom}
        onClose={onClose} onSent={() => {}} />
    </Suspense>
  )
}
